import { useEffect, useState } from 'react'
import { ArrowRightLeft, AlertTriangle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { showError } from '@/lib/error'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { UserPickerDialog } from '@/components/user-select'
import { useTransferUser } from '@/hooks/useUsers'
import type { UserListItem } from '@/api/users'

interface TransferUserDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	user: UserListItem | null
}

// 两段式：form 选接收人 → confirm 危险确认
export function TransferUserDialog({ open, onOpenChange, user }: TransferUserDialogProps) {
	const transferMutation = useTransferUser()
	const [phase, setPhase] = useState<'form' | 'confirm'>('form')
	const [receiver, setReceiver] = useState<UserListItem | null>(null)

	useEffect(() => {
		if (open) {
			setPhase('form')
			setReceiver(null)
		}
	}, [open, user])

	const fromName = user ? user.nickname || user.username : ''
	const toName = receiver ? receiver.nickname || receiver.username : ''

	const handleConfirm = async () => {
		if (!user || !receiver) return
		try {
			const counts = await transferMutation.mutateAsync({
				from_user_id: user.id,
				to_user_id: receiver.id,
			})
			toast.success(
				`转交完成：客户${counts.customers} · 跟踪${counts.trackers} · 送礼${counts.gifts} · 订单${counts.orders}`,
			)
			onOpenChange(false)
		} catch (error) {
			showError(error, '转交失败')
			setPhase('form')
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[440px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<ArrowRightLeft className="h-5 w-5" />
						员工整批转交
					</DialogTitle>
					<DialogDescription>
						将「{fromName}」名下全部客户、送礼记录、订单业绩转交给接收人
					</DialogDescription>
				</DialogHeader>

				{phase === 'form' ? (
					<>
						<div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
							<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
							<span>
								此操作为危险操作，将把该员工名下全部客户（锁定人/跟踪人）、送礼记录、订单业绩一并转交，操作不可逆，请谨慎操作。
							</span>
						</div>

						<div className="space-y-2">
							<label htmlFor="transfer-user-from" className="text-sm font-medium">转出员工</label>
							<Input id="transfer-user-from" value={fromName} disabled className="bg-muted" readOnly />
						</div>

						<div className="space-y-2">
							<label className="text-sm font-medium">转交给</label>
							<UserPickerDialog
								value={receiver}
								title="选择接收人"
								confirmText="选择"
								status={1}
								excludeUserIds={user ? [user.id] : undefined}
								onConfirm={(users) => setReceiver(users[0] ?? null)}
								trigger={
									<Button type="button" variant="outline" className="w-full justify-start">
										<span className={receiver ? 'truncate' : 'truncate text-muted-foreground'}>
											{receiver
												? `${receiver.username}${receiver.nickname ? ` · ${receiver.nickname}` : ''}`
												: '请选择接收人'}
										</span>
									</Button>
								}
							/>
						</div>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
								取消
							</Button>
							<Button type="button" variant="destructive" disabled={!receiver} onClick={() => setPhase('confirm')}>
								下一步
							</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
							<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
							<span>
								此操作将把「{fromName}」名下全部客户、送礼记录、订单业绩永久转交给「{toName}」，是否继续？
							</span>
						</div>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setPhase('form')}
								disabled={transferMutation.isPending}
							>
								返回
							</Button>
							<Button
								type="button"
								variant="destructive"
								onClick={handleConfirm}
								disabled={transferMutation.isPending}
							>
								{transferMutation.isPending && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								确认转交
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}
